/**
 * What a kept file is, as the web shows it (slice 5b spec §9): its kind's
 * word and mark, its size, and what can be uploaded (§5.3's types, 20 MB),
 * checked here first so a refusal says why at once; the Worker checks again.
 */
import {
  Archive,
  CalendarDays,
  File,
  FileImage,
  FileSpreadsheet,
  FileText,
  Presentation,
  type LucideIcon,
} from "lucide-react";

/** The largest file Crew keeps (§10). */
const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

/** The extensions Crew keeps, as the Worker's `uploadType` lists them. */
const EXTENSIONS = [
  ".pdf",
  ".txt",
  ".md",
  ".markdown",
  ".csv",
  ".json",
  ".html",
  ".htm",
  ".docx",
  ".xlsx",
  ".pptx",
  ".odt",
  ".ods",
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".heic",
  ".heif",
  ".ics",
  ".zip",
];

/** The file picker's `accept`. */
export const ACCEPT = EXTENSIONS.join(",");

const extOf = (name: string) => {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot).toLowerCase() : "";
};

/** Why Crew won't take this file, in a line, or null when it will. */
export function refusalFor(file: { name: string; size: number }): string | null {
  if (!EXTENSIONS.includes(extOf(file.name))) return "Crew can't keep this kind of file.";
  if (file.size > MAX_UPLOAD_BYTES) return "Too big: 20 MB at most.";
  if (file.size === 0) return "This file is empty.";
  return null;
}

/** A size in KB or MB, as Finder says it. */
export function sizeLabel(bytes: number): string {
  if (bytes < 1_000) return `${bytes} B`;
  if (bytes < 1_000_000) return `${Math.round(bytes / 1_000)} KB`;
  if (bytes < 1_000_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  return `${Number((bytes / 1_000_000_000).toFixed(1))} GB`;
}

export type FileKind = { label: string; icon: LucideIcon };

const SHEETS = ["sheet", "spreadsheet", "csv", "excel"];
const SLIDES = ["presentation", "powerpoint"];
const DOCS = ["wordprocessing", "opendocument.text", "msword", "markdown", "text/plain", "html"];

/** A file's kind, from its type (and its name when the type says little). */
export function kindOf(file: { mime: string; name: string }): FileKind {
  const mime = file.mime.toLowerCase();
  const ext = extOf(file.name);
  if (mime === "application/pdf" || ext === ".pdf") return { label: "PDF", icon: FileText };
  if (mime.startsWith("image/")) return { label: "Image", icon: FileImage };
  if (SHEETS.some((w) => mime.includes(w)) || [".xlsx", ".ods", ".csv"].includes(ext)) {
    return { label: "Spreadsheet", icon: FileSpreadsheet };
  }
  if (SLIDES.some((w) => mime.includes(w)) || ext === ".pptx") {
    return { label: "Slides", icon: Presentation };
  }
  if (mime === "text/calendar" || ext === ".ics") return { label: "Calendar", icon: CalendarDays };
  if (mime.includes("zip") && ext === ".zip") return { label: "Archive", icon: Archive };
  if (DOCS.some((w) => mime.includes(w)) || [".docx", ".odt", ".md", ".txt"].includes(ext)) {
    return { label: "Document", icon: FileText };
  }
  return { label: "File", icon: File };
}
